import type { TFunction } from "i18next";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";

import ButtonIcon from "../../images/rebrandIcons/ButtonIcon.png";

function createTourButtonHTML(label: string, direction: string | null = null, t: TFunction): string {
  const text = t(label);
  const arrowLeft = `<img class="h-2.5 mr-3 mb-1 transform rotate-180" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  const arrowRight = `<img class="h-2.5 ml-3 mb-1" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  return `
    <div class="flex gap-1 items-center">
      ${direction === "left" ? arrowLeft : ""}
      <p>${text}</p>
      ${direction === "right" ? arrowRight : ""}
    </div>
  `;
}

function useSetlistsCreateUpdateTour() {
  const { t } = useTranslation(["tutorial", "onboarding", "common"]);

  const startTour = useCallback((manual = false) => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "bg-white font-sentric rounded-[10px] text-gray-800 shadow-xl p-6",
        cancelIcon: { enabled: true },
        scrollTo: { behavior: "smooth", block: "center" },
      },
    });
    if (!manual) {
      tour.on("cancel", () => localStorage.setItem("setlistTourCompleted", "true"));
    }
    tour.on("complete", () => localStorage.setItem("setlistTourCompleted", "true"));

    // The Setlist Name Field Step
    tour.addStep({
      id: "setlistNameFieldTour",
      text: t("tutorial:setlists-setlist-name"),
      attachTo: {
        element: "#setlist-name-section",
        on: "right",
      },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 10,
      classes: "ml-2",
      buttons: [
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: () => tour.next(),
        },
      ],
    });

    // The Setlist Songs Field Step
    tour.addStep({
      id: "setlistSongsFieldTour",
      text: t("tutorial:setlists-add-songs"),
      attachTo: {
        element: "#setlist-songs-section",
        on: "right",
      },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 10,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: () => tour.back(),
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: () => tour.next(),
        },
      ],
    });

    // The Setlist Other Songs Section Step
    tour.addStep({
      id: "setlistOtherSongsSectionTour",
      text: t("tutorial:setlists-cover"),
      attachTo: {
        element: "#setlist-other-songs-section",
        on: "right",
      },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 10,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: () => tour.back(),
        },
        {
          text: createTourButtonHTML("common:next-button", "right", t),
          action: () => tour.next(),
        },
      ],
    });

    // The Setlist Notes Section Step
    tour.addStep({
      id: "setlistNotesSectionTour",
      text: t("tutorial:setlists-notes"),
      attachTo: {
        element: "#setlist-notes-section",
        on: "right",
      },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 10,
      buttons: [
        {
          text: createTourButtonHTML("onboarding:back-button", "left", t),
          action: () => tour.back(),
        },
        {
          text: createTourButtonHTML("onboarding:complete-button", null, t),
          action: () => {
            localStorage.setItem("setlistTourCompleted", "true");
            tour.complete();
          },
        },
      ],
    });

    void tour.start();
  }, [t]);

  return startTour;
}

export default useSetlistsCreateUpdateTour;
